import type {
  Appointment,
  AppointmentSlots,
  AppointmentStatusEvent,
  AssignChair,
  ChangeAppointmentStatus,
  CreateChair,
  CreateSeries,
  Chair,
  ChairAssignment,
  CreateAppointment,
  DayRoster,
  ExternalBlock,
  Month,
  EditAppointment,
  MoveAppointment,
  ProviderAvailability,
  SeriesResult,
  StaffMember,
  UpdateChair,
  Week,
} from "@dentacore/shared-types";

import { api } from "./client";
import { announceStockMoved } from "./stock-moved";

/**
 * The calendar's endpoints.
 *
 * **Branch-scoped, all of them** — unlike patients, which belong to the clinic.
 * An appointment happens in a chair, a chair belongs to a branch, and a branch
 * cannot move (settled decision 4.2). The header is checked against the
 * caller's memberships by `BranchScopeGuard` before any of these run.
 */

export function listAppointments(
  params: { from?: string; days?: number; patientId?: string } = {},
  signal?: AbortSignal,
): Promise<Appointment[]> {
  const query = new URLSearchParams();
  if (params.from !== undefined) query.set("from", params.from);
  if (params.days !== undefined) query.set("days", String(params.days));
  if (params.patientId !== undefined) query.set("patientId", params.patientId);

  const suffix = query.toString();
  return api<Appointment[]>(`/appointments${suffix === "" ? "" : `?${suffix}`}`, {
    branchScoped: true,
    signal,
  });
}

/**
 * Events on this branch's chair calendars that DentaCore did not create
 * (STORY-049b).
 *
 * **A separate call rather than a field on the appointment list** (G14). An
 * external block is not an appointment and cannot become one, and keeping the
 * two in different arrays is what makes counting a renovation as a booking hard
 * rather than easy.
 *
 * A clinic with no Google pairing gets an empty array, so the grid asks
 * unconditionally rather than branching on whether Google is set up.
 */
export function listExternalBlocks(
  params: { from?: string; days?: number } = {},
  signal?: AbortSignal,
): Promise<ExternalBlock[]> {
  const query = new URLSearchParams();
  if (params.from !== undefined) query.set("from", params.from);
  if (params.days !== undefined) query.set("days", String(params.days));

  const suffix = query.toString();
  return api<ExternalBlock[]>(`/appointments/external-blocks${suffix === "" ? "" : `?${suffix}`}`, {
    branchScoped: true,
    signal,
  });
}

/**
 * Book an appointment, or hold a chair with a buffer block.
 *
 * Omitting `patientId` makes it a block. **A refusal comes back as a 409 whose
 * body carries a sentence and a machine-readable `reason`**, so the screen can
 * show the sentence without parsing it.
 */
export function createAppointment(body: CreateAppointment): Promise<Appointment> {
  return api<Appointment>("/appointments", { method: "POST", body, branchScoped: true });
}

/**
 * Move a booking — the write behind dragging a block.
 *
 * **The server decides, every time.** The drag rejects an illegal drop before
 * the pointer is released, but that is an affordance built from occupancy the
 * server already sent; this call re-runs the real conflict check and the
 * exclusion constraint behind it. A drop the browser thought was fine can still
 * come back refused, and the block goes back where it was.
 */
export function moveAppointment(
  appointmentId: string,
  body: MoveAppointment,
): Promise<Appointment> {
  return api<Appointment>(`/appointments/${appointmentId}/move`, {
    method: "PATCH",
    body,
    branchScoped: true,
  });
}

/** Change what a booking is for, its teeth, who treats, or its description (QA4-D3). */
export function editAppointment(
  appointmentId: string,
  body: EditAppointment,
): Promise<Appointment> {
  return api<Appointment>(`/appointments/${appointmentId}`, {
    method: "PATCH",
    body,
    branchScoped: true,
  });
}

/**
 * The times a booking could move to — what the reschedule picker draws.
 *
 * **Read fresh every time the picker opens**, never cached: the list is the
 * chair as it was one round trip ago, and a colleague booking into it is
 * exactly the case the re-check at confirm time exists for.
 */
/**
 * The free times this booking could move to.
 *
 * **`onDate` and `chairId` reach another day and another room** (STORY-116).
 * Both default to the booking's own, which is what every caller wanted until
 * the picker learned to leave the day it was already on — the reason moving a
 * booking to another day was impossible through the interface even though the
 * move endpoint had always accepted one.
 */
export function listSlots(
  appointmentId: string,
  onDate?: string,
  chairId?: string,
  signal?: AbortSignal,
): Promise<AppointmentSlots> {
  const query = new URLSearchParams();
  if (onDate !== undefined) query.set("date", onDate);
  if (chairId !== undefined) query.set("chairId", chairId);
  const suffix = query.size === 0 ? "" : `?${query.toString()}`;

  return api<AppointmentSlots>(`/appointments/${appointmentId}/slots${suffix}`, {
    branchScoped: true,
    signal,
  });
}

/** The chairs in the active branch, including retired ones. */
export function listChairs(signal?: AbortSignal): Promise<Chair[]> {
  return api<Chair[]>("/chairs", { branchScoped: true, signal });
}

/**
 * Add a chair to the active branch. **Admin only.**
 *
 * `defaultTurnoverMinutes` is optional and stays that way: omitting it takes
 * the database column's default rather than a number written down in this
 * application. See CLAUDE.md, *Data is configured, not coded*.
 */
export function createChair(body: CreateChair): Promise<Chair> {
  return api<Chair>("/chairs", { method: "POST", body, branchScoped: true });
}

/**
 * Rename, reorder, retire or re-time a chair. **Admin only.**
 *
 * There is no delete — `isActive: false` is how a chair is removed from the
 * calendar without losing what it already carries. `chairId` reaches this by
 * id alone; the server still checks it belongs to the active branch.
 *
 * **A new `defaultTurnoverMinutes` reaches nothing already booked**: every
 * appointment stores the turnover it was created with, so this changes what
 * the next booking into that chair carries and leaves the diary alone.
 */
export function updateChair(chairId: string, body: UpdateChair): Promise<Chair> {
  return api<Chair>(`/chairs/${chairId}`, { method: "PATCH", body, branchScoped: true });
}

/**
 * The day's rota — every chair in the branch and who is on it.
 *
 * **The header comes from the server**, derived from the assignments. Building
 * it in the browser would be a second copy of a rule that changes daily.
 */
export function getRoster(date: string, signal?: AbortSignal): Promise<DayRoster> {
  return api<DayRoster>(`/chair-assignments?date=${date}`, { branchScoped: true, signal });
}

export function assignChair(body: AssignChair): Promise<ChairAssignment> {
  return api<ChairAssignment>("/chair-assignments", { method: "POST", body, branchScoped: true });
}

export function unassignChair(assignmentId: string): Promise<void> {
  return api<void>(`/chair-assignments/${assignmentId}`, {
    method: "DELETE",
    branchScoped: true,
  });
}

/**
 * The clinic's staff, each carrying the branches they work at.
 *
 * **Clinic-scoped**, unlike the rest of this file — a person belongs to the
 * clinic and works at branches, so filtering to a branch is the caller's job
 * and is done from `memberships`.
 */
export function listStaff(signal?: AbortSignal): Promise<StaffMember[]> {
  return api<StaffMember[]>("/users", { signal });
}

/**
 * Move a booking to another status.
 *
 * **The server decides.** The panel only offers the moves the graph allows, but
 * that is an affordance built from a mirror of the rule — this call is what
 * actually applies it, and an illegal move comes back as a 409 whose body
 * carries a sentence naming what *is* allowed.
 */
/**
 * Who moved this booking between statuses, and when (QA #39).
 *
 * ── Recorded since STORY-037, shown nowhere until now ───────────────────────
 *
 * Working plan §6.2 ends "**Every transition is recorded with actor and
 * timestamp**", and it always was — correctly, in the same transaction as the
 * move. The endpoint that reads it back was built too. **No screen had ever
 * called it**, so the trail existed only for whoever queried the database.
 *
 * That matters more here than for most audit trails: P3 Q4 let *any* signed-in
 * user change a status precisely because every change is attributable, and the
 * record is the control that makes the permissive rule safe. A control nobody
 * can see is not doing the job it was built for.
 */
export function listStatusEvents(
  appointmentId: string,
  signal?: AbortSignal,
): Promise<AppointmentStatusEvent[]> {
  // **Branch-scoped, like every other appointment route.** Omitting this asks
  // the server for an appointment without saying which branch it is in, and it
  // answers 400 — which the panel first rendered as "nothing recorded", because
  // it was catching the failure into an empty list. Both halves are fixed:
  // the header is sent, and a failed read now says so.
  return api<AppointmentStatusEvent[]>(`/appointments/${appointmentId}/status-events`, {
    branchScoped: true,
    signal,
  });
}

export async function changeAppointmentStatus(
  appointmentId: string,
  body: ChangeAppointmentStatus,
): Promise<{ id: string; status: string }> {
  const result = await api<{ id: string; status: string }>(
    `/appointments/${appointmentId}/status`,
    {
      method: "PATCH",
      body,
      branchScoped: true,
    },
  );
  // Completing a visit deducts its materials (STORY-111), which may make one low.
  if (result.status === "COMPLETED") announceStockMoved();
  return result;
}

/**
 * **Take one patient out of a family visit** (FV-D10): their booking is
 * cancelled and the ones after it move up. Answers with the bookings left.
 */
export function leaveVisit(appointmentId: string): Promise<Appointment[]> {
  return api<Appointment[]>(`/appointments/${appointmentId}/leave-visit`, {
    method: "POST",
    branchScoped: true,
  });
}

/** What both halves of ADR-0005's staff step return: the flag is down either way. */
export interface ValidationResult {
  id: string;
  awaitingValidation: boolean;
  validatedAt: string | null;
}

/**
 * A human has looked at an agent booking (ADR-0005, STORY-056).
 *
 * **Not an approval.** The appointment was already real and already held the
 * chair; this records that somebody at the practice has now seen it. Refused
 * with `NOT_AWAITING_VALIDATION` if it is not flagged, which is what stops two
 * people at the desk overwriting each other's attribution.
 */
/**
 * Send this patient the clinic's approved WhatsApp reminder for this booking.
 *
 * **Staff-initiated and one at a time.** Nothing schedules this; a reminder
 * exists because somebody at the desk looked at a booking and sent one.
 */
export function remindPatient(appointmentId: string): Promise<{ id: string }> {
  return api<{ id: string }>(`/appointments/${appointmentId}/remind`, {
    method: "POST",
    branchScoped: true,
  });
}

export function validateAppointment(appointmentId: string): Promise<ValidationResult> {
  return api<ValidationResult>(`/appointments/${appointmentId}/validate`, {
    method: "POST",
    branchScoped: true,
  });
}

/**
 * The time does not work, so the booking is cancelled — **with a reason**.
 *
 * It goes through the server's one status route, so it frees the slot, writes
 * the audit row, raises the `appointment.cancelled` event the patient is told
 * from, and deletes the Google event. It is not a delete here either.
 */
export function declineAppointment(
  appointmentId: string,
  reason: string,
): Promise<ValidationResult> {
  return api<ValidationResult>(`/appointments/${appointmentId}/decline`, {
    method: "POST",
    body: { reason },
    branchScoped: true,
  });
}

/**
 * Every booking in this branch still waiting for a human, whenever it is.
 *
 * **Not a window** (HC3), unlike `listAppointments`. The chip counts a booking
 * whatever date it falls on, so the list it opens has to be able to show one
 * next month — otherwise the header names something no screen can reach.
 */
export function listAwaitingValidation(signal?: AbortSignal): Promise<Appointment[]> {
  return api<Appointment[]>("/appointments/awaiting-validation", {
    branchScoped: true,
    signal,
  });
}

/**
 * Book a repeating course of treatment.
 *
 * **Read both lists.** The server books every occurrence that fits and returns
 * the ones that did not in `refused`, each naming its date and reason (P3 Q15).
 * A resolved promise means *some* occurrences were placed, never all of them.
 */
export function createSeries(body: CreateSeries): Promise<SeriesResult> {
  return api<SeriesResult>("/appointments/series", {
    method: "POST",
    body,
    branchScoped: true,
  });
}

/** Every occurrence of the series a booking belongs to. Empty means it has none. */
export function listSeries(appointmentId: string, signal?: AbortSignal): Promise<Appointment[]> {
  return api<Appointment[]>(`/appointments/${appointmentId}/series`, {
    branchScoped: true,
    signal,
  });
}

/**
 * Move every **future** occurrence of a series by the shift this one takes.
 *
 * Past occurrences are never rewritten (P3 Q6) — they carry invoices and charted
 * work. Occurrences that now clash come back in `refused` rather than rolling
 * back the ones that moved.
 */
export function moveSeries(appointmentId: string, startsAt: string): Promise<SeriesResult> {
  return api<SeriesResult>(`/appointments/${appointmentId}/series/move`, {
    method: "PATCH",
    body: { startsAt },
    branchScoped: true,
  });
}

/**
 * Who is working in this branch on a day.
 *
 * **Everyone is returned, available or not.** The picker offers the rostered and
 * still needs to say something useful about the rest — filtering server-side
 * would leave the screen unable to tell "not working" from "not here at all".
 */
export function listProviderAvailability(
  date: string,
  signal?: AbortSignal,
): Promise<ProviderAvailability[]> {
  return api<ProviderAvailability[]>(`/appointments/providers?date=${date}`, {
    branchScoped: true,
    signal,
  });
}

/**
 * Seven days at a glance, from the day given.
 *
 * **One request, not seven.** The clustering rule and the away strips both need
 * server-side data — appointments and the staff model — and asking per day would
 * be seven round trips for one screen.
 */
export function getWeek(from: string, signal?: AbortSignal): Promise<Week> {
  return api<Week>(`/appointments/week?from=${from}`, { branchScoped: true, signal });
}

/**
 * Load per day across a month, as `YYYY-MM`.
 *
 * **One request for the whole grid.** The month wants counts rather than the
 * week's clusters, so it has its own endpoint rather than five week calls whose
 * banding would be computed and discarded.
 */
export function getMonth(month: string, signal?: AbortSignal): Promise<Month> {
  return api<Month>(`/appointments/month?month=${month}`, { branchScoped: true, signal });
}
